import type { StateCreator } from 'zustand';
import type { HistoryEntry } from '../../types';

// ============================================================================
// HISTORY SLICE - Manages undo/redo functionality
// ============================================================================

export interface HistorySlice {
  past: HistoryEntry[];
  future: HistoryEntry[];
  maxHistorySize: number;

  // Actions
  pushHistory: (action: string, snapshot: any) => void;
  undo: () => HistoryEntry | null;
  redo: () => HistoryEntry | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;
}

export const createHistorySlice: StateCreator<HistorySlice> = (set, get, _api) => ({
  // Initial state
  past: [],
  future: [],
  maxHistorySize: 50,

  // Actions
  pushHistory: (action, snapshot) => {
    set((state) => {
      const entry: HistoryEntry = {
        timestamp: new Date(),
        action,
        snapshot,
      };

      const newPast = [...state.past, entry];

      // Limit history size
      if (newPast.length > state.maxHistorySize) {
        newPast.shift();
      }

      return {
        past: newPast,
        future: [], // Clear future when new action is performed
      };
    });
  },

  undo: () => {
    const { past } = get();
    if (past.length === 0) return null;

    const previousEntry = past[past.length - 1];

    set((state) => ({
      past: state.past.slice(0, -1),
      future: [previousEntry, ...state.future],
    }));

    return previousEntry;
  },

  redo: () => {
    const { future } = get();
    if (future.length === 0) return null;

    const nextEntry = future[0];

    set((state) => ({
      past: [...state.past, nextEntry],
      future: state.future.slice(1),
    }));

    return nextEntry;
  },

  canUndo: () => get().past.length > 0,

  canRedo: () => get().future.length > 0,

  clearHistory: () => set({ past: [], future: [] }),
});
